import { Link } from "react-router-dom";
import { BuyerTier, IBuyerScore, IBuyerScoreCriterion } from "../../utils/buyerScore";
import CheckIcon from "../../img/svg/checkIcon";
import XIcon from "../../img/svg/xIcon";
import ClockAnimation from "../../img/svg/clockAnimation";

const TIER_LABELS: { [key in BuyerTier]: string } = {
    bronze: "Bronze",
    prata: "Prata",
    ouro: "Ouro",
    diamante: "Diamante",
};

const TIER_COLORS: { [key in BuyerTier]: string } = {
    bronze: "bg-[#A0714B] text-white",
    prata: "bg-[#B0B7BF] text-primary",
    ouro: "bg-secondary text-primary",
    diamante: "bg-[#5EC9E8] text-primary",
};

export function BuyerScoreTierBadge({ tier, score }: { tier: BuyerTier, score: number }) {
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold whitespace-nowrap ${TIER_COLORS[tier]}`}>
            <span>{TIER_LABELS[tier]}</span>
            <span className="opacity-70 text-sm">{score}/100</span>
        </div>
    )
}

function formatCriterionDetail(criterion: IBuyerScoreCriterion) {
    switch (criterion.key) {
        case "accountAge":
            return `${criterion.rawValue} ${criterion.rawValue === 1 ? "mês" : "meses"}`;
        case "purchases":
            return `${criterion.rawValue} ${criterion.rawValue === 1 ? "compra" : "compras"}`;
        case "reviews":
            return `${criterion.rawValue} ${criterion.rawValue === 1 ? "avaliação" : "avaliações"}`;
        default:
            return criterion.met ? "verificado" : "não verificado";
    }
}

interface BuyerScoreCardProps {
    buyerScore: IBuyerScore | null;
    loading?: boolean;
    variant?: "full" | "compact";
    showLearnMoreLink?: boolean;
}

export default function BuyerScoreCard({ buyerScore, loading, variant = "full", showLearnMoreLink = true }: BuyerScoreCardProps) {

    if (loading) {
        return <ClockAnimation />
    }

    if (!buyerScore) {
        return <p className="text-center text-mono">Score do comprador não disponível</p>
    }

    return (
        <div className={`bg-moninho border border-mono/30 rounded p-4 w-full ${variant === "full" ? "max-w-lg m-auto" : ""}`}>
            <div className="flex items-center justify-between gap-4 mb-3">
                <h3 className="font-bold text-lg">Score do Comprador</h3>
                <BuyerScoreTierBadge tier={buyerScore.tier} score={buyerScore.score} />
            </div>
            <ul className="flex flex-col gap-2">
                {
                    buyerScore.breakdown.map(criterion => (
                        <li key={criterion.key} className="flex items-center justify-between gap-2 text-sm">
                            <span className="flex items-center gap-2">
                                {
                                    criterion.met ?
                                        <CheckIcon width="16px" fill="#00bb00" />
                                        : <XIcon width="16px" fill="#999" />
                                }
                                {criterion.label}
                                {
                                    variant === "full" ?
                                        <span className="text-mono">({formatCriterionDetail(criterion)})</span>
                                        : null
                                }
                            </span>
                            <span className="text-mono whitespace-nowrap">{Math.round(criterion.points)}/{criterion.maxPoints}</span>
                        </li>
                    ))
                }
            </ul>
            {
                showLearnMoreLink ?
                    <Link to="/buyer-score" className="block text-center text-secondary underline text-sm mt-3">O que é isso e como melhorar?</Link>
                    : null
            }
        </div>
    )
}
